import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CrossBrowseGridPreview from "../browse-puzzles/CrossBrowseGridPreview";
import {
    buildCrossBrowseGridPreviewFromRow,
    getHomeBrowsePuzzleTitle,
    getHomePuzzlePlayPath,
    getLocalizedHomeBrowseStoryTeaser,
} from "../browse-puzzles/homePuzzleRoUtils";
import JigsawService from "../puzzles/jigsaw/JigsawService";
import { getJigsawLandingFeaturedMeta } from "../puzzles/jigsaw/jigsawBrowseUtils";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { getContentLanguage } from "../../services/LangPrefs";
import { SURFACE } from "../../dark-theme/palette";
import { browseViewButtonSx } from "../browse-jigsaw/JigsawBrowseStoryCard";
import { usePagesTranslation } from "../locales";

function jigsawSelectPath(storyId) {
    const sid = typeof storyId === "string" ? storyId.trim() : "";
    if (!sid) return "/puzzles/jigsaw";
    return `/puzzle/play-jigsaw/${encodeURIComponent(sid)}`;
}

function crosswordTitle(row, language) {
    const storyTitle = getLocalizedHomeBrowseStoryTeaser(row, language)?.storyTitle;
    if (storyTitle) return storyTitle;
    const title = getHomeBrowsePuzzleTitle(row);
    return title && title !== "—" ? title : "";
}

function PuzzleCard({ typeLabel, title, to, children }) {
    const { t } = usePagesTranslation();
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                height: "100%",
                width: "100%",
                minWidth: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                bgcolor: SURFACE.card,
                borderColor: SURFACE.border,
                borderRadius: 2,
            }}
        >
            <Typography variant="overline" sx={{ letterSpacing: 1.2, color: "text.secondary", fontWeight: 700 }}>
                {typeLabel}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, minHeight: "2.6em" }} noWrap={false}>
                {title || "—"}
            </Typography>
            <Box
                sx={{
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: SURFACE.sunken,
                    border: "1px solid",
                    borderColor: SURFACE.border,
                    mb: 1.5,
                }}
            >
                {children}
            </Box>
            <Button
                component={RouterLink}
                to={to}
                variant="contained"
                sx={{ ...browseViewButtonSx, alignSelf: "flex-start", mt: "auto" }}
            >
                {t("landing.play-now")}
            </Button>
        </Paper>
    );
}

export default function DashboardDailyPuzzlesWidget() {
    const { t } = usePagesTranslation();
    const [loading, setLoading] = useState(true);
    const [jigsaw, setJigsaw] = useState(null);
    const [crossword, setCrossword] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const language = getContentLanguage();
            try {
                const [jigsawResult, crossPage] = await Promise.all([
                    JigsawService.getNewest({ language, excludeCompleted: false }),
                    HomeService.getHomePuzzlePage({
                        page: 0,
                        pageSize: 1,
                        language,
                        puzzleType: "CROSSWORD",
                        accessFilter: "all",
                    }),
                ]);
                if (cancelled) return;
                const meta = jigsawResult?.success
                    ? getJigsawLandingFeaturedMeta(jigsawResult.data)
                    : null;
                setJigsaw(meta);
                const row = crossPage?.content?.[0] || null;
                const preview = row ? buildCrossBrowseGridPreviewFromRow(row) : null;
                const playPath = row ? getHomePuzzlePlayPath(row) : null;
                setCrossword(
                    preview && playPath
                        ? { preview, playPath, title: crosswordTitle(row, language) }
                        : null,
                );
            } catch (e) {
                Logger.error("Dashboard daily puzzles:", e);
                if (!cancelled) {
                    setJigsaw(null);
                    setCrossword(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const card = (key, body) => (
        <Grid key={key} size={{ xs: 12, md: 4 }} sx={{ minWidth: 0, display: "flex" }}>
            {body}
        </Grid>
    );

    if (loading) {
        return card(
            "loading",
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
            </Box>,
        );
    }

    return (
        <>
            {jigsaw?.storyId
                ? card(
                    "jigsaw",
                    <PuzzleCard
                        typeLabel={t("nav.jigsaw")}
                        title={jigsaw.title}
                        to={jigsawSelectPath(jigsaw.storyId)}
                    >
                        {jigsaw.imageUrl ? (
                            <Box
                                component="img"
                                src={jigsaw.imageUrl}
                                alt=""
                                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                        ) : null}
                    </PuzzleCard>,
                )
                : null}
            {crossword
                ? card(
                    "crossword",
                    <PuzzleCard
                        typeLabel={t("nav.crossword")}
                        title={crossword.title}
                        to={crossword.playPath}
                    >
                        <Box sx={{ bgcolor: "#fff", width: "100%", height: "100%" }}>
                            <CrossBrowseGridPreview
                                grid={crossword.preview.grid}
                                filledCoordinates={crossword.preview.filledCoordinates}
                                words={crossword.preview.words}
                            />
                        </Box>
                    </PuzzleCard>,
                )
                : null}
        </>
    );
}
